import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { NotificationRuleFormData } from '@/features/notification-rules/schemas/create-notification-rule.schema.ts';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip.tsx';
import { Info } from 'lucide-react';
import { Input } from '@/components/ui/input.tsx';
import { useGetUsersQuery } from '@/features/users/hooks/queries/get-all-users.tsx';
import { useGetPlantsQuery } from '@/features/plants/hooks/queries/use-get-plants.tsx';
import { useGetPlantUsersQuery } from '@/features/plants/hooks/queries/[plantId]/[user]/use-get-plant-users.ts';
import { MultiSelect } from '@/components/ui/multi-select.tsx';
import { useSession } from '@/services/providers/session-provider.tsx';
import { usePermission } from '@/hooks/use-permission.ts';

export function CreateNotificationRuleOtherOptionsForm() {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const form = useFormContext<NotificationRuleFormData>();

  const canForwardTo = usePermission({
    resource: 'notification-rules',
    action: 'forwardTo',
  });

  return (
    <div className="space-y-2">
      <h2 className="font-semibold">Other options</h2>

      <FormField
        control={form.control}
        name="cooldownPeriod"
        render={({ field }) => (
          <FormItem>
            <div className="flex gap-2">
              <FormLabel className="required">Cooldown Period (minutes)</FormLabel>
              <TooltipProvider>
                <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground cursor-help touch-manipulation transition-colors"
                      onClick={(e) => {
                        e.preventDefault();
                        setTooltipOpen(!tooltipOpen);
                      }}
                      onMouseEnter={() => setTooltipOpen(true)}
                      onMouseLeave={() => setTooltipOpen(false)}
                    >
                      <Info size={14} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="text-sm">
                      Minimum time interval between notifications. Prevents spam by ensuring
                      notifications are only sent once per cooldown period, even if conditions
                      are met multiple times.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <FormControl>
              <Input
                type="number"
                placeholder="60"
                {...field}
                onChange={(e) => field.onChange(Number(e.target.value))}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {canForwardTo && <ForwardToSelectFormNotificationRules />}
    </div>
  );
}

function ForwardToSelectFormNotificationRules() {
  const { email } = useSession();
  const form = useFormContext<NotificationRuleFormData>();
  const watchSelectedPlantId = form.watch('plantId');

  const { data: users, isLoading: areUsersLoading } = useGetUsersQuery();
  const { data: plants, isLoading: arePlantsLoading } = useGetPlantsQuery({
    includeOverview: false,
  });

  const selectedPlant = plants?.find((p) => p.plantId === watchSelectedPlantId);

  const { data: availableUserIds, isLoading: arePlantUsersLoading } = useGetPlantUsersQuery({
    plantId: selectedPlant?.plantId || '',
    enabled: Boolean(selectedPlant?.plantId),
  });

  const availableUsers = users
    ?.filter(
      (user) =>
        (availableUserIds?.userIds?.includes(user.userId) ||
          user.role === 'Company administrator' ||
          user.role === 'Company owner') &&
        user.email !== email
    )
    ?.map((user) => ({
      value: user.userId,
      label: user.email,
      icon: undefined,
    }));

  const isLoading = areUsersLoading || arePlantsLoading || arePlantUsersLoading;

  return (
    <FormField
      control={form.control}
      name="forwardTo"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Forward To</FormLabel>
          <MultiSelect
            options={availableUsers ?? []}
            onValueChange={field.onChange}
            defaultValue={field.value}
            placeholder={isLoading ? 'Loading users...' : 'Select users'}
            variant="inverted"
            maxCount={3}
            disabled={isLoading}
          />
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
